import React, { useEffect, useState } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { StatCard } from '@/components/StatCard';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import {
  Users,
  UserCheck,
  UserCircle,
  TrendingUp,
  DollarSign,
  ArrowUpRight,
  Calendar as CalendarIcon,
  Filter,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const COLORS = ['#10b981', '#8b5cf6', '#3b82f6', '#f59e0b', '#ef4444', '#06b6d4'];

export default function DashboardPage() {
  const [metrics, setMetrics] = useState(null);
  const [leadsByStatus, setLeadsByStatus] = useState([]);
  const [leadsBySource, setLeadsBySource] = useState([]);
  const [dailyLeads, setDailyLeads] = useState([]);
  const [salesMetrics, setSalesMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quickStats, setQuickStats] = useState({ today_new: 0, today_existing: 0, today_total: 0, week_new: 0, week_existing: 0, week_total: 0, month_new: 0, month_existing: 0, month_total: 0 });
  
  // Filtros de período
  const [dateFilter, setDateFilter] = useState('all');
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [filteredMetrics, setFilteredMetrics] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchFilteredMetrics();
  }, [dateFilter, customDateRange]);

  const fetchData = async () => {
    try {
      const results = await Promise.allSettled([
        api.getDashboardMetrics(),
        api.getLeadsByStatus(),
        api.getLeadsBySource(),
        api.getDailyLeads(30),
        api.getSalesMetrics(),
        api.getDashboardMetrics({ period: 'today' }),
        api.getDashboardMetrics({ period: 'week' }),
        api.getDashboardMetrics({ period: 'month' }),
      ]);
      const val = (i) => results[i].status === 'fulfilled' ? results[i].value : null;
      setMetrics(val(0)?.data || null);
      setLeadsByStatus(val(1)?.data || []);
      setLeadsBySource(val(2)?.data || []);
      setDailyLeads(val(3)?.data || []);
      setSalesMetrics(val(4)?.data || null);
      setQuickStats({
        today: val(5)?.data?.total_leads || 0,
        today_existing: val(5)?.data?.existing_clients || 0,
        week: val(6)?.data?.total_leads || 0,
        week_existing: val(6)?.data?.existing_clients || 0,
        month: val(7)?.data?.total_leads || 0,
        month_existing: val(7)?.data?.existing_clients || 0,
      });
    } catch (error) {
      toast.error('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredMetrics = async () => {
    if (dateFilter === 'all') {
      setFilteredMetrics(null);
      return;
    }
    try {
      const params = {};
      if (dateFilter === 'today') params.period = 'today';
      else if (dateFilter === 'week') params.period = 'week';
      else if (dateFilter === 'month') params.period = 'month';
      else if (dateFilter === 'custom' && customDateRange.from) {
        params.date_from = format(customDateRange.from, 'yyyy-MM-dd');
        params.date_to = format(customDateRange.to || customDateRange.from, 'yyyy-MM-dd');
      } else return;
      const r = await api.getDashboardMetrics(params);
      setFilteredMetrics(r.data);
    } catch {
      setFilteredMetrics(null);
    }
  };

  const getFilterLabel = () => {
    switch (dateFilter) {
      case 'today': return 'Hoje';
      case 'week': return 'Esta Semana';
      case 'month': return 'Este Mês';
      case 'custom': 
        if (customDateRange.from && customDateRange.to) {
          return `${format(customDateRange.from, 'dd/MM')} - ${format(customDateRange.to, 'dd/MM')}`;
        }
        return 'Personalizado';
      default: return 'Todos';
    }
  };

  const currentMetrics = dateFilter === 'all' ? metrics : filteredMetrics;

  if (loading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto" />
            <p className="mt-4 text-slate-600">Carregando...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
      <Sidebar />
      <main className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Painel Biotop
              </h1>
              <p className="text-slate-600" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Suplementos naturais para sua qualidade de vida
              </p>
            </div>

            {/* Filtros de Período */}
            <div className="flex items-center gap-2">
              <div className="flex bg-white rounded-lg border p-1">
                <Button
                  variant={dateFilter === 'all' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setDateFilter('all')}
                  className={dateFilter === 'all' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                >
                  Todos
                </Button>
                <Button
                  variant={dateFilter === 'today' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setDateFilter('today')}
                  className={dateFilter === 'today' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                >
                  Hoje
                </Button>
                <Button
                  variant={dateFilter === 'week' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setDateFilter('week')}
                  className={dateFilter === 'week' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                >
                  Semana
                </Button>
                <Button
                  variant={dateFilter === 'month' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setDateFilter('month')}
                  className={dateFilter === 'month' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                >
                  Mês
                </Button>
              </div>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    Personalizado
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="range"
                    selected={customDateRange}
                    onSelect={(range) => {
                      setCustomDateRange(range || { from: null, to: null });
                      if (range?.from && range?.to) {
                        setDateFilter('custom');
                      }
                    }}
                    locale={ptBR}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Badge do período selecionado */}
          {dateFilter !== 'all' && (
            <div className="mb-4">
              <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-emerald-50">
                <Filter className="w-3 h-3 mr-1" />
                Filtro: {getFilterLabel()}
                <button 
                  onClick={() => setDateFilter('all')} 
                  className="ml-2 hover:text-red-600"
                >
                  ✕
                </button>
              </Badge>
            </div>
          )}

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <StatCard
              title="Total de Leads"
              value={currentMetrics?.total_leads || 0}
              icon={Users}
              color="emerald"
            />
            <StatCard
              title="Novos Leads"
              value={currentMetrics?.new_leads || 0}
              icon={UserCheck}
              color="blue"
            />
            <StatCard
              title="Clientes Existentes"
              value={currentMetrics?.existing_clients || 0}
              icon={UserCircle}
              color="purple"
            />
          </div>

          {/* Resumo de Vendas */}
          {salesMetrics && (
            <div className="mb-8" data-testid="sales-metrics-section">
              <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Resumo de Vendas
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                <Card className="p-4 border-l-4 border-l-amber-500" data-testid="sales-today-pending">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Em Analise Hoje</p>
                      <h3 className="text-2xl font-bold text-amber-700 mt-1">{salesMetrics.today?.pending || 0}</h3>
                      <p className="text-sm text-slate-500 mt-0.5">
                        R$ {(salesMetrics.today?.pending_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <CalendarIcon className="w-8 h-8 text-amber-400" />
                  </div>
                </Card>
                <Card className="p-4 border-l-4 border-l-emerald-500" data-testid="sales-today-approved">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Aprovadas Hoje</p>
                      <h3 className="text-2xl font-bold text-emerald-700 mt-1">{salesMetrics.today?.approved || 0}</h3>
                      <p className="text-sm text-slate-500 mt-0.5">
                        R$ {(salesMetrics.today?.approved_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-emerald-400" />
                  </div>
                </Card>
                <Card className="p-4 border-l-4 border-l-red-500" data-testid="sales-today-rejected">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Reprovadas Hoje</p>
                      <h3 className="text-2xl font-bold text-red-700 mt-1">{salesMetrics.today?.rejected || 0}</h3>
                      <p className="text-sm text-slate-500 mt-0.5">
                        R$ {(salesMetrics.today?.rejected_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <XCircle className="w-8 h-8 text-red-400" />
                  </div>
                </Card>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Card className="p-4 border-l-4 border-l-amber-500" data-testid="sales-month-pending">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Em Analise no Mes</p>
                      <h3 className="text-2xl font-bold text-amber-700 mt-1">{salesMetrics.month?.pending || 0}</h3>
                      <p className="text-sm text-slate-500 mt-0.5">
                        R$ {(salesMetrics.month?.pending_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <CalendarIcon className="w-8 h-8 text-amber-400" />
                  </div>
                </Card>
                <Card className="p-4 border-l-4 border-l-emerald-500" data-testid="sales-month-approved">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Aprovadas no Mes</p>
                      <h3 className="text-2xl font-bold text-emerald-700 mt-1">{salesMetrics.month?.approved || 0}</h3>
                      <p className="text-sm text-slate-500 mt-0.5">
                        R$ {(salesMetrics.month?.approved_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-emerald-400" />
                  </div>
                </Card>
                <Card className="p-4 border-l-4 border-l-red-500" data-testid="sales-month-rejected">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Reprovadas no Mes</p>
                      <h3 className="text-2xl font-bold text-red-700 mt-1">{salesMetrics.month?.rejected || 0}</h3>
                      <p className="text-sm text-slate-500 mt-0.5">
                        R$ {(salesMetrics.month?.rejected_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <XCircle className="w-8 h-8 text-red-400" />
                  </div>
                </Card>
              </div>
            </div>
          )}

          {/* Resumo Rápido - Tabulacoes Novos vs Existentes */}
          <div className="mb-8">
            <Card className="overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Periodo</TableHead>
                    <TableHead className="font-semibold text-center text-blue-700">Leads Novos</TableHead>
                    <TableHead className="font-semibold text-center text-purple-700">Clientes Existentes</TableHead>
                    <TableHead className="font-semibold text-center text-slate-700">Total Atividade</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow>
                    <TableCell className="font-medium">Hoje</TableCell>
                    <TableCell className="text-center text-lg font-bold text-blue-600" data-testid="tab-today-new">{quickStats.today}</TableCell>
                    <TableCell className="text-center text-lg font-bold text-purple-600" data-testid="tab-today-existing">{quickStats.today_existing}</TableCell>
                    <TableCell className="text-center text-lg font-bold text-slate-800">{quickStats.today + quickStats.today_existing}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Esta Semana</TableCell>
                    <TableCell className="text-center text-lg font-bold text-blue-600" data-testid="tab-week-new">{quickStats.week}</TableCell>
                    <TableCell className="text-center text-lg font-bold text-purple-600" data-testid="tab-week-existing">{quickStats.week_existing}</TableCell>
                    <TableCell className="text-center text-lg font-bold text-slate-800">{quickStats.week + quickStats.week_existing}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="font-medium">Este Mes</TableCell>
                    <TableCell className="text-center text-lg font-bold text-blue-600" data-testid="tab-month-new">{quickStats.month}</TableCell>
                    <TableCell className="text-center text-lg font-bold text-purple-600" data-testid="tab-month-existing">{quickStats.month_existing}</TableCell>
                    <TableCell className="text-center text-lg font-bold text-slate-800">{quickStats.month + quickStats.month_existing}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Card>
            <p className="text-xs text-slate-400 mt-2">
              Leads Novos = primeiro contato no periodo | Clientes Existentes = ja estavam no sistema e mandaram mensagem no periodo
            </p>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Daily Leads Chart */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Leads diários (30 dias)
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dailyLeads}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#10b981" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Card>

            {/* Leads by Status */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Leads por Status
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={leadsByStatus}
                    dataKey="count"
                    nameKey="status"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ status, count }) => `${status}: ${count}`}
                  >
                    {leadsByStatus.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* Leads by Source */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Leads por Origem
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={leadsBySource}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="source" stroke="#64748b" />
                <YAxis stroke="#64748b" />
                <Tooltip />
                <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          {/* Status Cards */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-6">
              <p className="text-sm font-medium text-slate-600">Leads Contactados</p>
              <h3 className="text-3xl font-bold text-slate-900 mt-2">
                {currentMetrics?.contacted_leads || 0}
              </h3>
            </Card>

            <Card className="p-6">
              <p className="text-sm font-medium text-slate-600">Leads Qualificados</p>
              <h3 className="text-3xl font-bold text-slate-900 mt-2">
                {currentMetrics?.qualified_leads || 0}
              </h3>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
